import type { CollateralKey } from "@/data/fixtures";
import {
	findConsensusCluster,
	type PriceQuote,
} from "@/lib/oracle/oracle-consensus";

export const COLLATERAL_KEYS: CollateralKey[] = ["ICP", "ckBTC", "ckETH"];

export type ExternalPriceSamples = Partial<Record<CollateralKey, number[]>>;

export type ProviderQuoteRow = {
	provider: string;
	providerLabel: string;
	asset: CollateralKey;
	price: number | null;
	error: string | null;
};

/** Labels for UI transparency — aligned with keeper functional providers + public extras */
export const PROVIDER_LABELS: Record<string, string> = {
	coinbase: "Coinbase",
	coingecko: "CoinGecko",
	coinmarketcap: "CoinMarketCap",
	redstone: "RedStone",
	pyth: "Pyth",
	kraken: "Kraken",
	binance: "Binance",
	coincap: "CoinCap",
	chainlink: "Chainlink",
};

const COINGECKO_IDS: Record<CollateralKey, string> = {
	ICP: "internet-computer",
	ckBTC: "bitcoin",
	ckETH: "ethereum",
};

const COINBASE_SYMBOLS: Record<CollateralKey, string> = {
	ICP: "ICP-USD",
	ckBTC: "BTC-USD",
	ckETH: "ETH-USD",
};

function coingeckoBase(): string {
	return import.meta.env.DEV
		? "/price-api/coingecko"
		: "https://api.coingecko.com";
}

function coinbaseBase(): string {
	return import.meta.env.DEV ? "/price-api/coinbase" : "https://api.coinbase.com";
}

function extractJsonNumber(body: string, key: string): number | null {
	const needle = `"${key}"`;
	const idx = body.indexOf(needle);
	if (idx < 0) return null;
	const rest = body.slice(idx + needle.length);
	const afterColon = rest.split(":")[1];
	if (!afterColon) return null;
	const trimmed = afterColon.trim();
	const numStr = trimmed
		.replace(/^"/, "")
		.match(/^[\d.]+/)?.[0];
	if (!numStr) return null;
	const n = Number.parseFloat(numStr);
	return Number.isFinite(n) && n > 0 ? n : null;
}

async function fetchJson(
	url: string,
	headers: Record<string, string> = {},
): Promise<unknown | null> {
	try {
		const res = await fetch(url, {
			headers: { Accept: "application/json", ...headers },
		});
		if (!res.ok) return null;
		return await res.json();
	} catch {
		return null;
	}
}

async function fetchJsonText(url: string): Promise<string | null> {
	try {
		const res = await fetch(url, { headers: { Accept: "application/json" } });
		if (!res.ok) return null;
		return await res.text();
	} catch {
		return null;
	}
}

function parsePositive(value: unknown): number | null {
	const n = Number.parseFloat(String(value));
	return Number.isFinite(n) && n > 0 ? n : null;
}

function row(
	provider: string,
	asset: CollateralKey,
	price: number | null,
	error: string | null = price == null ? "unavailable" : null,
): ProviderQuoteRow {
	return {
		provider,
		providerLabel: PROVIDER_LABELS[provider] ?? provider,
		asset,
		price,
		error,
	};
}

async function fetchCoinGeckoPrices(): Promise<ProviderQuoteRow[]> {
	const ids = Object.values(COINGECKO_IDS).join(",");
	const apiKey = import.meta.env.VITE_COINGECKO_API_KEY as string | undefined;
	const keyParam = apiKey
		? `&x_cg_demo_api_key=${encodeURIComponent(apiKey)}`
		: "";
	const body = await fetchJsonText(
		`${coingeckoBase()}/api/v3/simple/price?vs_currencies=usd&ids=${ids}${keyParam}`,
	);
	if (!body) {
		return COLLATERAL_KEYS.map((asset) =>
			row("coingecko", asset, null, "fetch failed"),
		);
	}
	const out: ProviderQuoteRow[] = [];
	for (const asset of COLLATERAL_KEYS) {
		const id = COINGECKO_IDS[asset];
		const nestedKey = `"${id}":{"usd":`;
		const idx = body.indexOf(nestedKey);
		if (idx < 0) {
			out.push(row("coingecko", asset, null));
			continue;
		}
		const slice = body.slice(idx + nestedKey.length);
		const numStr = slice.match(/^[\d.]+/)?.[0];
		const price = numStr ? parsePositive(numStr) : null;
		out.push(row("coingecko", asset, price));
	}
	return out;
}

async function fetchCoinbasePrice(asset: CollateralKey): Promise<number | null> {
	const pair = COINBASE_SYMBOLS[asset];
	const body = await fetchJsonText(`${coinbaseBase()}/v2/prices/${pair}/spot`);
	if (!body) return null;
	return extractJsonNumber(body, "amount");
}

async function fetchCoinbasePrices(): Promise<ProviderQuoteRow[]> {
	const entries = await Promise.all(
		COLLATERAL_KEYS.map(
			async (asset) => [asset, await fetchCoinbasePrice(asset)] as const,
		),
	);
	return entries.map(([asset, price]) => row("coinbase", asset, price));
}

async function fetchKrakenPrices(): Promise<ProviderQuoteRow[]> {
	const data = (await fetchJson(
		"https://api.kraken.com/0/public/Ticker?pair=ICPUSD,XXBTZUSD,XETHZUSD",
	)) as {
		result?: Record<string, { c?: [string] }>;
	} | null;
	if (!data?.result) {
		return COLLATERAL_KEYS.map((asset) =>
			row("kraken", asset, null, "fetch failed"),
		);
	}
	return [
		row("kraken", "ICP", parsePositive(data.result.ICPUSD?.c?.[0])),
		row("kraken", "ckBTC", parsePositive(data.result.XXBTZUSD?.c?.[0])),
		row("kraken", "ckETH", parsePositive(data.result.XETHZUSD?.c?.[0])),
	];
}

async function fetchCoinCapPrices(): Promise<ProviderQuoteRow[]> {
	const apiKey = (
		import.meta.env.VITE_COINCAP_API_KEY as string | undefined
	)?.trim();
	if (!apiKey) {
		return COLLATERAL_KEYS.map((asset) =>
			row("coincap", asset, null, "missing CoinCap key"),
		);
	}
	const assets: { id: string; asset: CollateralKey }[] = [
		{ id: "internet-computer", asset: "ICP" },
		{ id: "bitcoin", asset: "ckBTC" },
		{ id: "ethereum", asset: "ckETH" },
	];
	return Promise.all(
		assets.map(async ({ id, asset }) => {
			const data = (await fetchJson(
				`https://rest.coincap.io/v3/assets/${id}`,
				{ Authorization: `Bearer ${apiKey}` },
			)) as { data?: { priceUsd?: string } } | null;
			return row("coincap", asset, parsePositive(data?.data?.priceUsd));
		}),
	);
}

async function fetchBinancePrices(): Promise<ProviderQuoteRow[]> {
	const symbols: { symbol: string; asset: CollateralKey }[] = [
		{ symbol: "ICPUSDT", asset: "ICP" },
		{ symbol: "BTCUSDT", asset: "ckBTC" },
		{ symbol: "ETHUSDT", asset: "ckETH" },
	];
	return Promise.all(
		symbols.map(async ({ symbol, asset }) => {
			const data = (await fetchJson(
				`https://api.binance.com/api/v3/ticker/price?symbol=${symbol}`,
			)) as { price?: string } | null;
			return row("binance", asset, parsePositive(data?.price));
		}),
	);
}

type ProviderFetch = () => Promise<ProviderQuoteRow[]>;

/** UI transparency order: functional keeper core first, then public extras. */
const EXTERNAL_PROVIDERS: { id: string; fetch: ProviderFetch }[] = [
	{ id: "coinbase", fetch: fetchCoinbasePrices },
	{ id: "coingecko", fetch: fetchCoinGeckoPrices },
	{ id: "kraken", fetch: fetchKrakenPrices },
	{ id: "coincap", fetch: fetchCoinCapPrices },
	{ id: "binance", fetch: fetchBinancePrices },
];

/** Fetch USD quotes from all external providers in parallel. */
export async function fetchExternalPriceSamples(): Promise<ExternalPriceSamples> {
	const quotes = await fetchAllProviderQuotes();
	const samples: ExternalPriceSamples = { ICP: [], ckBTC: [], ckETH: [] };
	for (const asset of COLLATERAL_KEYS) {
		for (const q of quotes[asset] ?? []) {
			if (q.price != null) samples[asset]?.push(q.price);
		}
	}
	return samples;
}

/** Per-provider quotes grouped by collateral asset. */
export async function fetchAllProviderQuotes(): Promise<
	Record<CollateralKey, ProviderQuoteRow[]>
> {
	const results = await Promise.allSettled(
		EXTERNAL_PROVIDERS.map((p) => p.fetch()),
	);
	const byAsset: Record<CollateralKey, ProviderQuoteRow[]> = {
		ICP: [],
		ckBTC: [],
		ckETH: [],
	};
	for (const result of results) {
		if (result.status !== "fulfilled") continue;
		for (const quote of result.value) {
			byAsset[quote.asset].push(quote);
		}
	}
	return byAsset;
}

export function consensusForAsset(quotes: ProviderQuoteRow[]) {
	const priced: PriceQuote[] = quotes
		.filter((q) => q.price != null)
		.map((q) => ({ provider: q.provider, price: q.price! }));
	const consensus = findConsensusCluster(priced);
	const inConsensus = new Set(consensus?.cluster.map((q) => q.provider) ?? []);
	return { consensus, inConsensus };
}
